import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'lib-multi-lot-search-control',
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
  ],
  template: `
    <div class="search-container">
      @let hasMatchedResults = matchedIndices().length > 0;
      <mat-form-field appearance="outline" class="search-field">
        @if (!hasMatchedResults) {
          <mat-icon class="search-icon" matPrefix>search</mat-icon>
        }

        <mat-label>{{ label() }}</mat-label>
        <input
          matInput
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()"
          [placeholder]="placeholder()"
        />
      </mat-form-field>

      @if (hasMatchedResults) {
        <div class="match-controls">
          <span class="match-count mat-label-medium">
            {{ currentIndex() + 1 }} of {{ matchedIndices().length }}
          </span>
          <button
            mat-icon-button
            (click)="onPrevious()"
            [disabled]="matchedIndices().length === 0"
            class="small-icon-button"
            aria-label="previous"
          >
            <mat-icon class="icon">keyboard_arrow_up</mat-icon>
          </button>
          <button
            mat-icon-button
            (click)="onNext()"
            [disabled]="matchedIndices().length === 0"
            class="small-icon-button"
            aria-label="next"
          >
            <mat-icon class="icon">keyboard_arrow_down</mat-icon>
          </button>
        </div>
      }
    </div>
  `,
  styles: `
    @use '@angular/material' as mat;
    @use 'theme' as theme;

    .search-container {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 1rem;
      background-color: #{mat.get-theme-color(theme.$light-theme, surface)};
      border-bottom: 1px solid #{mat.get-theme-color(
          theme.$light-theme,
          surface-variant
        )};

      .search-field {
        flex: 1;
        margin-bottom: -1.25em;
      }

      .match-controls {
        display: flex;
        align-items: center;
        gap: 2px;

        .match-count {
          white-space: nowrap;
          padding: 0 8px;
        }

        .small-icon-button {
          width: 32px;
          height: 32px;
          padding: 0;

          .icon {
            font-size: 20px;
            width: 20px;
            height: 20px;
          }
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiLotSearchControlComponent {
  label = input<string>('Search for SPI');
  placeholder = input<string>('Enter part of a SPI');
  searchableItems = input<string[]>([]);

  searchTerm = model<string>('');

  indexChange = output<number>();

  currentIndex = signal<number>(0);

  matchedIndices = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();

    const items = this.searchableItems();
    return items
      .map((item, index) => ({
        index,
        value: item?.toLowerCase() ?? '',
      }))
      .filter(({ value }) => value.includes(term))
      .map(({ index }) => index);
  });

  onSearchChange(): void {
    this.currentIndex.set(0);
    this.emitIndexChange();
  }

  onNext(): void {
    this.navigateToIndex('next');
  }

  onPrevious(): void {
    this.navigateToIndex('previous');
  }

  private navigateToIndex(direction: 'next' | 'previous'): void {
    const indices = this.matchedIndices();
    if (indices.length === 0) return;

    const currentIdx = this.currentIndex();
    const newIndex =
      direction === 'next'
        ? (currentIdx + 1) % indices.length
        : (currentIdx - 1 + indices.length) % indices.length;

    this.currentIndex.set(newIndex);
    this.emitIndexChange();
  }

  private emitIndexChange(): void {
    const indices = this.matchedIndices();
    if (indices.length > 0 && this.currentIndex() < indices.length) {
      this.indexChange.emit(indices[this.currentIndex()]);
    }
  }
}
